import { useNostr } from "@nostrify/react";
import { useQuery } from "@tanstack/react-query";
import { SCORE_KIND, SCORE_TAG, parseScoreEvent, type ScoreEntry } from "@/game/leaderboard";

export interface UseLeaderboardOptions {
  /** Only include victorious runs. */
  victoriesOnly?: boolean;
  /** Max entries to display after ranking. */
  top?: number;
}

export function useLeaderboard(options: UseLeaderboardOptions = {}) {
  const { nostr } = useNostr();
  const { victoriesOnly = false, top = 25 } = options;

  return useQuery<ScoreEntry[]>({
    queryKey: ["bch-trail", "leaderboard", victoriesOnly, top],
    queryFn: async (c) => {
      const signal = AbortSignal.any([c.signal, AbortSignal.timeout(4000)]);
      const events = await nostr.query(
        [{ kinds: [SCORE_KIND], "#t": [SCORE_TAG], limit: 300 }],
        { signal },
      );

      const entries = events
        .map(parseScoreEvent)
        .filter((e): e is ScoreEntry => e !== null)
        .filter((e) => (victoriesOnly ? e.outcome === "victory" : true));

      // Keep only each pubkey's best score so the board isn't dominated by one player.
      const bestByPubkey = new Map<string, ScoreEntry>();
      for (const e of entries) {
        const existing = bestByPubkey.get(e.pubkey);
        if (!existing || e.score > existing.score) {
          bestByPubkey.set(e.pubkey, e);
        }
      }

      return Array.from(bestByPubkey.values())
        .sort((a, b) => b.score - a.score)
        .slice(0, top);
    },
    staleTime: 30_000,
  });
}
